import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useOrgBranding } from '@/hooks/use-org-branding'
import { getApiErrorMessage } from '@/lib/api-error'
import { enableDemoReset } from '@/lib/feature-flags'
import { type DemoResetResult, resetDemoTenant } from '@/services/portal.service'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Loader2, RotateCcw } from 'lucide-react'
import { useState } from 'react'

// The only demo tenant this button is ever allowed to touch. Mirrors
// app.services.demo_reset_service.DEMO_SLUG -- this is a UI-layer
// convenience gate, not the security boundary. The server independently
// refuses in production and pins to this same slug regardless of what the
// client sends (the request carries no organization id or slug at all).
const DEMO_ORG_SLUG = 'delgo'

/**
 * "Reset demo state" button for the Delgo demo tenant. Clears seeded
 * employees and severance packages, then re-imports the packaged roster
 * fixture, so a live demo can be replayed from its starting state without
 * anyone running `scripts/seed_delgo.py --reset` from a laptop.
 *
 * Self-gates two ways before rendering anything:
 *   - `enableDemoReset` (VITE_ENABLE_DEMO_RESET) must be true. Defaults to
 *     false, and must never be set true in a `.env.production.*` or
 *     `.env.gcp-prod.*` file.
 *   - the signed-in org's slug must be exactly `DEMO_ORG_SLUG`.
 *
 * Below both gates, the component renders nothing -- no button, no dialog,
 * no path that reaches `resetDemoTenant()`.
 */
export function DemoResetButton() {
  const { slug } = useOrgBranding()
  const [open, setOpen] = useState(false)
  const [result, setResult] = useState<DemoResetResult | null>(null)
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: resetDemoTenant,
    onSuccess: (data) => {
      setResult(data)
      // Every portal screen showing org data (People, Import History,
      // Elections, Cost & Savings, HR System) is stale the moment this
      // resolves -- the tenant's people and packages were just replaced.
      // A full invalidate is deliberate: this is a rare, confirmation-gated
      // action, not a hot path worth scoping query keys for.
      queryClient.invalidateQueries()
    },
  })

  if (!enableDemoReset || slug !== DEMO_ORG_SLUG) {
    return null
  }

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      setResult(null)
      mutation.reset()
    }
  }

  const handleConfirm = () => {
    mutation.mutate()
  }

  return (
    <>
      <Button variant="destructive" onClick={() => setOpen(true)}>
        <RotateCcw className="h-4 w-4 mr-2" />
        Reset demo state
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Delgo demo data</DialogTitle>
            <DialogDescription>
              This clears every seeded employee and severance package in the Delgo demo tenant and
              re-imports the demo roster, so it's ready to replay from the start. HR admin accounts
              are kept.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
            <p className="text-sm text-amber-800 font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              This action cannot be undone.
            </p>
          </div>

          {result && <p className="text-sm text-muted-foreground">{result.message}</p>}

          {mutation.isError && (
            <p className="text-sm text-destructive" role="alert">
              {getApiErrorMessage(mutation.error, 'Could not reset the demo tenant.')}
            </p>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={mutation.isPending}
            >
              {result ? 'Close' : 'Cancel'}
            </Button>
            {!result && (
              <Button variant="destructive" onClick={handleConfirm} disabled={mutation.isPending}>
                {mutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  'Reset demo data'
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
